import { useMutation, useQuery } from '@apollo/client';
import { Plus, Star } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CatalogItemFormDialog } from '../CatalogItemFormDialog';
import { CatalogSectionHeader } from '../CatalogSectionHeader';
import { ServiceConsumablesEditor } from './ServiceConsumablesEditor';
import { SERVICES_QUERY, SET_SERVICE_STATUS_MUTATION } from './services.gql';

import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface Service {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category_id: string | null;
  tax_rate_id: string | null;
  price_cents: string;
  is_quick_key: boolean;
  branch_id: string | null;
  status: 'active' | 'inactive' | 'discontinued';
  created_at: string;
  updated_at: string;
}

interface ServicesResponse {
  services: {
    items: Service[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

export function ServicesPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_write = user?.permissions.includes('catalog.services.write') ?? false;
  const is_admin = user?.roles.includes('administrator') ?? false;
  const [quick_key_only, setQuickKeyOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [consumables_target, setConsumablesTarget] = useState<Service | null>(null);

  const { data, loading, refetch } = useQuery<ServicesResponse>(SERVICES_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
      search: list.search || null,
      status_filter: list.status_filter,
      quick_key_only: quick_key_only || null,
    },
  });

  const [setStatus] = useMutation(SET_SERVICE_STATUS_MUTATION);

  const onClickToggleStatus = async (row: Service): Promise<void> => {
    try {
      const result = await setStatus({
        variables: { id: row.id, status: row.status === 'active' ? 'inactive' : 'active' },
      });
      assertMutationSucceeded(result, 'update_service_status_failed');
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const columns: CrudColumn<Service>[] = [
    {
      key: 'code',
      header_key: 'catalog.services.code',
      width: 'w-32',
      render: (r) => <span className="font-mono text-xs">{r.code}</span>,
    },
    {
      key: 'name',
      header_key: 'catalog.services.name',
      render: (r) => (
        <div className="flex items-center gap-2">
          {r.is_quick_key ? <Star className="text-accent h-3.5 w-3.5" aria-hidden /> : null}
          <span className="font-medium">{r.name}</span>
        </div>
      ),
    },
    {
      key: 'price',
      header_key: 'catalog.services.price',
      align: 'right',
      width: 'w-32',
      render: (r) => <span className="tabular-nums">{formatCentsString(r.price_cents)}</span>,
    },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-32',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-64',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {can_write ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setConsumablesTarget(r)}
            >
              {t('service_consumables.edit_action')}
            </Button>
          ) : null}
          {can_write ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onClickToggleStatus(r);
              }}
            >
              {r.status === 'active' ? t('common.deactivate') : t('common.activate')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <CatalogSectionHeader
        title={t('catalog.services.title')}
        subtitle={t('catalog.services.subtitle')}
      />

      <CrudTable<Service>
        rows={data?.services.items ?? []}
        columns={columns}
        loading={loading}
        page={data?.services.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="catalog.services.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            filters={
              <label className="text-muted-foreground flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={quick_key_only}
                  onChange={(e) => setQuickKeyOnly(e.target.checked)}
                />
                {t('catalog.services.quick_key_filter')}
              </label>
            }
            primary_action={
              is_admin ? (
                <Button type="button" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('catalog.item_form.new_item')}</span>
                </Button>
              ) : undefined
            }
          />
        }
      />

      {creating ? (
        <CatalogItemFormDialog
          initial_kind="service"
          onClose={() => setCreating(false)}
          onSaved={async (kind) => {
            setCreating(false);
            showToast({
              tone: 'success',
              title: t('alerts.create_success_title'),
              body: t('alerts.create_success_body'),
            });
            if (kind === 'service') {
              await refetch();
            } else {
              navigate('/app/catalog/products');
            }
          }}
        />
      ) : null}

      {consumables_target ? (
        <ServiceConsumablesEditor
          service_id={consumables_target.id}
          service_name={consumables_target.name}
          onClose={() => setConsumablesTarget(null)}
        />
      ) : null}
    </div>
  );
}
